import type { AnyItem } from '#layers/data-model/public';
import type { UseM2MStateReturn } from './use-m2m-state';
import { isCreatedItem } from '#layers/forms/public';

type M2MRowActionsInputs = Pick<
	UseM2MStateReturn,
	'getTargetReferenceKeyFromJunction' | 'connectTarget' | 'disconnectTarget' | 'undoCreateTarget' | 'getItemState'
>;

/**
 * Remove/undo behaviour for a single M2M junction row, shared by every M2M list
 * input (generic list, users). Callers locate the junction themselves — the
 * generic list matches by target item, the user picker by primary key — then hand
 * the row here so the create/connect/disconnect branching lives in one place.
 */
export function useM2MRowActions(m2mState: M2MRowActionsInputs) {
	const { getTargetReferenceKeyFromJunction, connectTarget, disconnectTarget, undoCreateTarget, getItemState } = m2mState;

	function removeJunction(junctionItem: AnyItem) {
		// A pending connect (not yet saved) is undone; an existing junction emits a `_delete`.
		if (isCreatedItem(junctionItem)) {
			undoCreateTarget(junctionItem);
			return;
		}

		const targetReferenceKey = getTargetReferenceKeyFromJunction(junctionItem);
		if (targetReferenceKey) disconnectTarget(targetReferenceKey);
	}

	function undoJunction(junctionItem: AnyItem) {
		const targetReferenceKey = getTargetReferenceKeyFromJunction(junctionItem);

		switch (getItemState(junctionItem)) {
			case 'created':
			case 'connected':
				undoCreateTarget(junctionItem);
				break;
			case 'disconnected':
				if (targetReferenceKey) connectTarget(targetReferenceKey);
				break;
		}
	}

	return { removeJunction, undoJunction };
}
